import type React from 'react';

import { Text } from '@change/ds-core-ui/Text';
import { CheckIcon } from '@change/ds-icons/CheckIcon';
import { LinkIcon } from '@change/ds-icons/LinkIcon';
import { tv } from '@change/ds-utils';

import { type TranslatableLabel, useResolvedLabel } from '#src/app/shared/hooks/i18n';
import type { ShareAction as ShareActionType, ShareChannel } from '#src/app/shared/hooks/share';

import { CopyLinkStatus } from '../CopyLinkStatus';
import { GuardedCopyButton } from '../GuardedCopyButton';

import { useCopyLinkVisibleUrlButton } from './hooks/useCopyLinkVisibleUrlButton';

type SupportedShareChannels = ShareChannel;
type ShareButtonProps = Readonly<{
	buttonConfig: {
		ShareAction: ShareActionType['ShareAction'];
		channel: SupportedShareChannels;
		label: TranslatableLabel;
		successLabel?: TranslatableLabel;
		url: string;
		/** The URL the share action copies, when it differs from `url`. */
		shortUrl?: string;
		urlDescription?: TranslatableLabel;
	};
}> &
	React.ComponentPropsWithoutRef<'div'>;

export const CopyLinkVisibleUrlButton: React.FC<ShareButtonProps> = ({
	buttonConfig: { ShareAction, label, successLabel, url, shortUrl, urlDescription },
	...rest
}) => {
	const { data, actions } = useCopyLinkVisibleUrlButton({ label, successLabel, url, shortUrl });
	const buttonLabel = useResolvedLabel(data.buttonLabel);
	// Stays "Copy link" in the copied state: a name change on the focused button is announced on top of
	// `CopyLinkStatus` in some screen readers
	const accessibleLabel = useResolvedLabel(label);
	const resolvedDescription = useResolvedLabel(urlDescription);

	const buttonClass = tv({
		base: 'flex',
		variants: {
			clicked: {
				true: 'text-typography-success dark:text-typography-strong-fixed',
				false: 'text-icon-strong-fixed',
			},
		},
	});

	return (
		<div className="border-stroke-weaker elevation-low flex flex-col gap-3 rounded-lg border px-5 py-4" {...rest}>
			<div className="bg-fill-weaker rounded-md px-7 py-3 text-xs">
				<div className="text-typography-strong leading-5">{resolvedDescription}</div>
				<div className="text-typography-strong overflow-hidden text-xs leading-6 font-bold text-ellipsis whitespace-nowrap select-none">
					{data.resolvedUrl}
				</div>
			</div>

			<ShareAction onAfterAction={actions.afterShare}>
				<GuardedCopyButton
					onPress={actions.press}
					aria-label={accessibleLabel}
					leadingIcon={
						data.clickedState ? (
							<CheckIcon data-qa="copied-icon-check" className="text-icon-success dark:text-icon-strong-fixed" />
						) : (
							<LinkIcon />
						)
					}
					variant="primary"
					className={buttonClass({ clicked: data.clickedState })}
				>
					<Text size="medium" fontWeight="bold" className="flex gap-1.5">
						{buttonLabel}
					</Text>
				</GuardedCopyButton>
			</ShareAction>
			<CopyLinkStatus announcementKey={data.copyAnnouncementKey} />
		</div>
	);
};
